import type { MetaFunction } from "react-router"

import { ButtonLink } from "~/components/ui/button"
import { BlogLayout, BlogProse } from "~/components/blog/BlogLayout"
import { BlogCta } from "~/components/blog/BlogCta"
import { WrittenBy } from "~/components/blog/WrittenBy"
import {
  A,
  Figure,
  H2,
  H3,
  Lead,
  List,
  P,
  Quote,
  Strong,
  Table,
} from "~/components/blog/prose"
import { getPost, postMeta } from "~/data/posts"

// Doc-concept links, as set in the source document.
const CONCEPTS = "https://console.pine59.com/docs/concepts"
const LINKS = {
  tradeArea: `${CONCEPTS}/metrics/trade-area-place`,
  place: `${CONCEPTS}/location-types/place`,
  area: `${CONCEPTS}/location-types/area`,
  footTraffic: `${CONCEPTS}/metrics/foot-traffic-place`,
  visitorDemographics: `${CONCEPTS}/metrics/visitor-demographics-place`,
  visitorProfile: `${CONCEPTS}/metrics/places_spatialai_visitor_profile`,
  visitorJourney: `${CONCEPTS}/metrics/places_visitor_journey`,
  datasets: "https://console.pine59.com/docs/datasets",
}

const post = getPost("what-is-a-catchment-area")!

export const meta: MetaFunction = ({ location }) =>
  postMeta(post, location.pathname)

export default function Post() {
  return (
    <BlogLayout post={post}>
      <BlogProse>
        <WrittenBy post={post} />

        <Lead>
          Understanding the geographic reach of a location is not simply a
          question of distance. Accessibility, transport networks, surrounding
          destinations, and everyday movement patterns all influence where
          visitors come from and how far a location's reach extends.
        </Lead>
        <P>
          Catchment area analysis provides a way to describe and measure that
          relationship. Depending on the method, a catchment can represent
          anything from an estimated radius or travel-time boundary to the areas
          that visitors are attracted from.
        </P>

        <BlogCta />

        <H2>What is a catchment area?</H2>
        <P>
          A catchment area is the geographic area from which a physical location
          attracts its visitors. It describes the relationship between a point
          of interest (POI) and the resident or work areas of its visitors.
        </P>
        <P>
          Catchments can vary significantly even between locations that are
          geographically close. A location next to a major transport corridor
          may attract visitors from a wider area, while another nearby location
          may have a much more local pattern.
        </P>
        <P>
          Several factors shape this reach. Location type influences how far
          people are willing to travel, while roads, public transport,
          geographic barriers, and surrounding alternatives affect accessibility
          and visitor choice. Urban and rural locations may also show very
          different patterns because of differences in population density,
          transport options, and typical travel distances. Daily routines, such
          as commuting and workplace visits, can further extend a catchment
          beyond residential areas.
        </P>
        <P>
          <Strong>
            For this reason, how a catchment area is defined depends largely on
            how it is measured.
          </Strong>
        </P>

        <H2>Catchment area vs. trade area</H2>
        <P>
          Catchment area and trade area describe closely related concepts and
          are often used interchangeably.
        </P>
        <P>
          Catchment area is the broader term for the geography from which a
          location attracts visitors. Trade area is particularly common in
          commercial and location-based analysis, where the focus is on visitor
          origins and market reach.
        </P>
        <P>
          At Pine59, Trade Area refers to the dataset used to understand visitor
          origins based on mobility patterns.
        </P>

        <H2>Why catchment areas matter</H2>
        <P>
          Catchment area analysis helps connect a physical location with the
          geographic patterns behind its visitation. It can support decisions
          such as:
        </P>
        <List
          items={[
            <>
              <Strong>Location and site planning:</Strong> understand the reach
              of existing locations and evaluate potential new ones.
            </>,
            <>
              <Strong>Network planning:</Strong> identify geographic gaps,
              overlaps, and opportunities across multiple locations.
            </>,
            <>
              <Strong>Marketing and audience planning:</Strong> understand which
              areas contribute visitors and where to focus local activity.
            </>,
            <>
              <Strong>Competitive and cannibalization analysis:</Strong>{" "}
              evaluate overlap between locations and surrounding alternatives.
            </>,
            <>
              <Strong>Performance analysis:</Strong> compare geographic reach and
              monitor how visitor patterns change over time.
            </>,
          ]}
        />

        <H2>Three ways to determine a catchment area</H2>
        <P>
          There is no single method for defining a catchment area. The
          appropriate approach depends on whether{" "}
          <Strong>
            the goal is to estimate accessibility or understand observed visitor
            behavior.
          </Strong>
        </P>

        <Figure
          src="/art/blog/what-is-a-catchment-area/three-methods.webp"
          alt="Three maps of the same location showing a catchment area defined three ways: a five-mile radius circle, a drive-time boundary shaped by the road network, and an observed-mobility heatmap of where visitors actually come from."
          caption="The same location, three catchment definitions: a radius, a drive-time boundary, and observed mobility."
          width={1774}
          height={887}
        />

        <H3>1. Buffer or radius</H3>
        <P>
          The simplest method uses a fixed-distance radius around a location.
          For example, everything within five kilometers might be treated as
          part of the catchment.
        </P>
        <P>
          This approach is easy to calculate and useful for high-level
          estimates, but it assumes that distance affects accessibility equally
          in every direction. It does not account for road networks, geographic
          barriers, transportation options, or actual visitor behavior.
        </P>

        <H3>2. Drive-time or walk-time</H3>
        <P>
          Travel-time catchments use the surrounding transportation network to
          define which areas can be reached within a given period, such as a
          10-minute drive or a 15-minute walk.
        </P>
        <P>
          The resulting boundary reflects accessibility more realistically than
          a fixed radius because it accounts for the structure of roads and
          paths.
        </P>
        <P>
          However, it still represents potential accessibility. It shows where
          people could reasonably travel from, rather than where visitors
          actually originate.
        </P>

        <H3>3. Mobility-based</H3>
        <P>
          A mobility-based approach derives the catchment from observed
          visitation and movement patterns rather than a predefined geographic
          boundary.
        </P>
        <P>
          Aggregated location signals can be used to understand the areas
          associated with visitors to a location. This can reveal patterns that
          geometric models do not capture, such as workplace-driven visitation,
          commuting behavior, or catchments that extend along particular
          transport corridors.
        </P>
        <P>
          Unlike radius and travel-time methods, this approach derives
          geographic reach from observed mobility patterns rather than
          accessibility alone. Its main requirement is access to reliable
          mobility data.
        </P>

        <Table
          head={["Method", "How it works", "Main limitation"]}
          rows={[
            [
              "Buffer / radius",
              "Fixed-distance boundary around a location",
              "Does not account for accessibility or behavior",
            ],
            [
              "Drive-time / walk-time",
              "Boundary based on travel time through a transport network",
              "Represents potential rather than observed visitation",
            ],
            [
              "Mobility-based",
              "Geographic distribution derived from observed mobility patterns",
              "Requires reliable mobility data",
            ],
          ]}
        />

        <H2>How Pine59 supports catchment area analysis</H2>
        <P>
          Pine59 <A href={LINKS.tradeArea}>Trade Area</A> provides a data-driven
          view of visitor origins based on observed mobility patterns rather
          than a predefined geographic boundary.
        </P>
        <P>
          Visitors are attributed to inferred home and work areas, with each
          origin area reported as a share of the location's visits. Home and
          work origins are analyzed separately, helping distinguish residential
          patterns from those associated with workplaces and commuting.
        </P>
        <P>
          Privacy thresholds, geographic resolution, methodology, coverage, and
          update cadence vary by dataset and market and should be considered
          when interpreting results.
        </P>
        <P>
          Catchment-related analysis in Pine59 can be applied to individual{" "}
          <A href={LINKS.place}>Places</A> as well as bounded geographic{" "}
          <A href={LINKS.area}>Areas</A>. Metrics can also be attributed to
          custom geographic boundaries, such as trade zones, property outlines,
          or existing catchment polygons.
        </P>
        <P>
          Trade Area answers the core catchment question: where are visitors
          coming from? Other Pine59 datasets add context:
        </P>
        <List
          items={[
            <>
              <A href={LINKS.footTraffic}>Foot Traffic</A> measures visit
              volume.
            </>,
            <>
              <A href={LINKS.visitorDemographics}>Visitor Demographics</A>{" "}
              describes the demographic composition of visitors, while{" "}
              <A href={LINKS.visitorProfile}>Visitor Profile</A> describes their
              lifestyle and interest profiles.
            </>,
            <>
              <A href={LINKS.visitorJourney}>Visitor Journey</A> shows where
              visitors go before and after a location.
            </>,
          ]}
        />
        <P>
          Together, these datasets provide a broader view of where visitors come
          from, how much visitation a location receives, who those visitors are,
          and how they move.
        </P>
        <P>
          Coverage and available metrics vary by dataset and market. Current
          availability can be found in the{" "}
          <A href={LINKS.datasets}>dataset documentation</A>.
        </P>

        <H2>See what catchment analysis could reveal about your locations</H2>
        <Quote>
          A radius can tell you what is nearby. A drive-time model can tell
          you what is accessible. Mobility data can show you the geographic
          patterns behind actual visitation.
        </Quote>
        <P>
          With Pine59, you can go beyond assumed boundaries to understand where
          visitors come from, how much visitation a location receives, who those
          visitors are, and how they move between places.
        </P>
        <P>
          Explore the data visually in Pine59 Insights, or access Pine59
          datasets through the API to use location intelligence in your own
          models, products, and workflows.
        </P>
        <P>
          Want to see how this could work for your locations, markets, or use
          case?
        </P>
        <div className="mt-8">
          <ButtonLink href="/get-started" withArrow>
            Book a meeting
          </ButtonLink>
        </div>
      </BlogProse>
    </BlogLayout>
  )
}
